import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { BsThreeDots } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import DropDown from 'components/inputs/DropDown';
import Select from 'components/inputs/Select';
import CheckBox from 'components/inputs/CheckBox';
import ReactPagination from 'components/general/ReactPagination';
import Footer from 'components/general/Footer';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { API } from 'api';
import { FETCH_FLEET_RESERVATIONS } from 'actions/fleetReservation';
import FleetReservationCreate from './FleetReservationCreate';
import FleetReservationActionModal from './FleetReservationActionModal';
import {
  StatusPill,
  canManageFleetReservations,
  canRescheduleRow,
  canCancelRow,
  connectorLabel,
  formatDate,
  formatWindow,
  formatTime,
  STATUS_FILTER_OPTIONS,
  loadStations,
  toStationOptions,
  asArray,
  unwrap,
  DateField,
} from './fleetReservationUtils';

const COLUMNS = ['Code', 'Fleet', 'Station', 'Charger', 'Conn.', 'Date', 'Booked', 'Blocked From', 'Status', ''];
const initialFilters = { fleetOwnerId: '', stationId: '', status: '', dateFrom: '', dateTo: '', upcomingOnly: false };

const FleetReservationList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { navigateTo } = useContext(NavContext);
  const { results, isLoading, page, limit, total, totalPages } = useSelector((state) => state.fleetReservation);
  const rolePermissionData = useSelector((state) => state.manageRole.manageRoles);
  const profileData = useSelector((state) => state.profile.userProfile);
  const canManage = canManageFleetReservations(profileData, rolePermissionData);
  const [filters, setFilters] = useState(initialFilters);
  const [fleetOption, setFleetOption] = useState(null);
  const [stations, setStations] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [rowAction, setRowAction] = useState(null);

  const getList = useCallback(
    (params = {}) => {
      const query = _.omitBy({ page: 1, limit, ...filters, ...params }, (value) => value === '' || value === false);
      dispatch({ type: FETCH_FLEET_RESERVATIONS.REQUEST, payload: query });
    },
    [filters, limit]
  );

  useEffect(() => {
    getList();
  }, [filters]);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    loadStations()
      .then(setStations)
      .catch(() => setStations([]));
  }, []);

  const loadFleetOptions = useMemo(
    () =>
      _.debounce((search, callback) => {
        API.getFleetReservationFleetOptions({ search, limit: 20 })
          .then((res) => callback(asArray(unwrap(_.get(res, 'data'))).map((owner) => ({ label: owner.label || owner.name, value: owner.ownerId }))))
          .catch(() => callback([]));
      }, 300),
    []
  );
  useEffect(() => () => loadFleetOptions.cancel(), []);
  const stationOptions = useMemo(() => [{ label: 'All stations', value: '' }, ...toStationOptions(stations)], [stations]);

  const openRowAction = (row, action) => setRowAction({ action, reservationCode: row.reservationCode, occurrenceDate: row.reservationDate });

  const showSkeleton = isLoading && _.isEmpty(results);

  return (
    <LayoutContainer>
      <AdminHeader title={t('header.fleetReservation', 'Fleet Reservation')} />
      <SkeletonTheme baseColor="#1b2a38" highlightColor="#092235">
        <div className="page-content-wrapper scrollable fr-page">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div className="fr-subtitle">Reserved charging capacity by date. One row is one charger on one date.</div>
            {canManage && <Button onClick={() => setShowCreate(true)}>+ Create Reservation</Button>}
          </div>

          <Card>
            <div className="fr-toolbar">
              <Select
                className="fr-toolbar__field"
                label="Fleet"
                isAsync
                isClearable
                cacheOptions
                defaultOptions
                loadOptions={loadFleetOptions}
                placeholder="All fleets"
                value={fleetOption}
                onChange={(option) => {
                  setFleetOption(option);
                  setFilter('fleetOwnerId', _.get(option, 'value', ''));
                }}
              />
              <Select
                className="fr-toolbar__field"
                label="Station"
                placeholder="All stations"
                options={stationOptions}
                value={filters.stationId}
                onChange={(value) => setFilter('stationId', value || '')}
                disableSort
              />
              <Select
                className="fr-toolbar__field"
                label="Status"
                placeholder="All statuses"
                options={STATUS_FILTER_OPTIONS}
                value={filters.status}
                onChange={(value) => setFilter('status', value || '')}
                disableSort
              />
              <div>
                <div className="fr-label">Date range</div>
                <div className="fr-range">
                  <DateField value={filters.dateFrom} placeholder="From" onChange={(value) => setFilter('dateFrom', value)} />
                  <span>–</span>
                  <DateField value={filters.dateTo} min={filters.dateFrom} placeholder="To" onChange={(value) => setFilter('dateTo', value)} />
                </div>
              </div>
              <CheckBox
                id="fr-upcoming"
                containerClassName="fr-upcoming"
                checked={filters.upcomingOnly}
                onChange={(e) => setFilter('upcomingOnly', e.target.checked)}
              >
                Upcoming only
              </CheckBox>
            </div>
          </Card>

          <Card>
            <div className={`table-responsive ${isLoading && !showSkeleton ? 'fr-refreshing' : ''}`}>
              <table className="record-list-table">
                <thead>
                  <tr>
                    {COLUMNS.map((heading) => (
                      <th key={heading}>{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {showSkeleton ? (
                    _.times(5, (index) => (
                      <tr key={index}>
                        <td colSpan={COLUMNS.length}>
                          <Skeleton height={15} />
                        </td>
                      </tr>
                    ))
                  ) : _.isEmpty(results) ? (
                    <tr>
                      <td colSpan={COLUMNS.length} className="border-0">
                        <div className="empty-data-block">No reservations found</div>
                      </td>
                    </tr>
                  ) : (
                    results.map((row) => (
                      <tr key={row.id} className="fr-clickable" onClick={() => navigateTo(`/fleet-reservations/${row.reservationCode}`)}>
                        <td className="fr-code">{row.reservationCode}</td>
                        <td>
                          {row.fleetName}
                          {row.memberName && <div className="fr-muted small">{row.memberName}</div>}
                        </td>
                        <td>{row.stationName}</td>
                        <td>{row.chargerOcppId || '—'}</td>
                        <td>{connectorLabel(row.connectorId, row.connectorName) || '—'}</td>
                        <td>{formatDate(row.reservationDate, 'YYYY-MM-DD')}</td>
                        <td>{formatWindow(row.bookedStartTime, row.bookedEndTime)}</td>
                        <td className="fr-blocked">{formatTime(row.blockStartTime)}</td>
                        <td>
                          <StatusPill status={row.status} />
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {canManage && (canRescheduleRow(row) || canCancelRow(row)) ? (
                            <DropDown popupClass="bottom-left" trigger={<BsThreeDots size={20} className="fr-more" />}>
                              <ul>
                                {canRescheduleRow(row) && (
                                  <li className="item" onClick={() => openRowAction(row, 'reschedule')}>
                                    Reschedule
                                  </li>
                                )}
                                {canCancelRow(row) && (
                                  <li className="item" onClick={() => openRowAction(row, 'cancel')}>
                                    Cancel Reservation
                                  </li>
                                )}
                              </ul>
                            </DropDown>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {!isLoading && total > 0 && (
              <ReactPagination
                currentPage={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                handlePageClick={(selected) => getList({ page: selected.selected + 1 })}
                marginPagesDisplayed={1}
              />
            )}
          </Card>
        </div>
      </SkeletonTheme>

      {rowAction && (
        <FleetReservationActionModal
          {...rowAction}
          onClose={() => setRowAction(null)}
          onDone={() => {
            setRowAction(null);
            getList({ page });
          }}
        />
      )}
      {showCreate && (
        <FleetReservationCreate
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            getList();
          }}
        />
      )}
      <Footer />
    </LayoutContainer>
  );
};

export default FleetReservationList;
